/**
 * Training Compliance Certificate — one person, printable.
 *
 * A REGULATORY RECORD, not an award: an auditor has to be able to verify every line of it.
 * Three rules follow from that and are easy to undo by accident:
 *
 *  1. The score always prints its DENOMINATOR ("8 signed ÷ 11 required"). A bare percentage is
 *     not auditable.
 *  2. Gaps are printed, never summarised away — the record table lists EVERY document with its
 *     own status, so an unsigned one is named in place rather than folded into a total. A
 *     certificate that hides its failures is the one thing that would discredit this feature.
 *     (There was once a second "Open items" block restating the unsigned rows; on a real record
 *     that repeated 33 of 44 entries, so the table alone carries the rule now.)
 *  3. Dates are rendered EXACTLY as the tenant returned them. A filter result reads day-first
 *     and a QW_Get_Object item reads month-first (Critical Note #24), so reformatting here
 *     would shift a date by up to eleven months on one of the two sources.
 *
 * The certificate id is derived from the person + the snapshot's build time, so reprinting the
 * same snapshot yields a byte-identical certificate id — that is what makes it citable.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { formatStamp as formatStampUtc, stampId } from '../utils/datetime';
import '../styles/TrainingCertificate.css';

/** Signed first, then draft, open, and the outright gaps last — so a reader scanning down the
 *  record table arrives at what is outstanding rather than hunting for it. */
const STATE_ORDER = { done: 0, draft: 1, open: 2, miss: 3 };

const STATE_PILL = {
  done: { cls: 'p-ok', label: 'Signed' },
  open: { cls: 'p-open', label: 'In work' },
  draft: { cls: 'p-open', label: 'Draft' },
  miss: { cls: 'p-miss', label: 'No task' },
};

/** A stable, human-readable stamp for the certificate id — UTC, so two viewers of the same
    certificate cannot read two different ids off it. */
const stampOf = (builtAt) => stampId(builtAt);

const formatStamp = (value) => (value ? formatStampUtc(value, { fallback: null }) : null);

const dash = (v) => {
  const s = String(v == null ? '' : v).trim();
  return s && s.toLowerCase() !== 'none' ? s : '—';
};

/**
 * `single` switches to the ONE-TRAINING record: `{ doc, task }` for a person who has signed
 * that document. It is deliberately not a compliance statement — it certifies one signature on
 * one revision and says so in as many words, because a sheet headed with someone's name is
 * otherwise read as "this person is trained", full stop.
 */
function TrainingCertificate({ person, panelName, builtAt, setupId, onClose, single }) {
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [approval, setApproval] = useState(null);   // the TRN's QA sign-off, single mode only

  // Read the person's own USRP item for the fields the report can't carry (department,
  // employee id, manager…). Enrichment only — a failure here never blocks the certificate.
  useEffect(() => {
    let cancelled = false;
    setLoadingProfile(true);
    fetch(`/api/training/${setupId}/person/${encodeURIComponent(person.uid)}/profile`,
      { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled) { setProfile(data); setLoadingProfile(false); } })
      .catch(() => { if (!cancelled) { setProfile(null); setLoadingProfile(false); } });
    return () => { cancelled = true; };
  }, [setupId, person.uid]);

  // The QA signature is NOT this certificate's own claim — it is read off the training record
  // (TRN) the task belongs to, and only when that record's routing state is Approved. A trainee
  // signs the task; QA signs the record. Anything else leaves the line blank to be signed by hand.
  const approvalTask = (single && single.task && single.task.tk) || null;
  useEffect(() => {
    if (!approvalTask) { setApproval(null); return undefined; }
    let cancelled = false;
    fetch(`/api/training/${setupId}/task/${encodeURIComponent(approvalTask)}/approval`,
      { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => { if (!cancelled) setApproval(data); })
      .catch(() => { if (!cancelled) setApproval(null); });
    return () => { cancelled = true; };
  }, [setupId, approvalTask]);

  // The print stylesheet keys off this class, so Ctrl+P prints the record and not the app
  // behind it — the toolbar button and the browser's own shortcut behave identically.
  useEffect(() => {
    document.body.classList.add('trc-print-mode');
    return () => document.body.classList.remove('trc-print-mode');
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const print = useCallback(() => window.print(), []);

  const rows = useMemo(
    () => [...(person.docs || [])].sort((a, b) => (
      (STATE_ORDER[a.st] ?? 9) - (STATE_ORDER[b.st] ?? 9)
      || String(a.k || '').localeCompare(String(b.k || ''))
    )),
    [person.docs],
  );

  /* Does this person's record carry any quiz at all? The column appears only if so, so a
     certificate for an account that runs no quizzes prints exactly as it always did.

     A quiz is reported here, never scored on: the compliance verdict above is the SIGNATURE
     rate, and folding a quiz into it would change what an existing certificate claims. What
     it must not do is stay silent — a record that prints a signature while hiding a failed
     comprehension check would overstate, which is the one thing this document cannot do. */
  const anyQuiz = rows.some((d) => d.q);
  const quizPassed = rows.filter((d) => d.q && d.q.state === 'passed').length;
  const quizOwed = rows.filter((d) => d.q && d.q.state !== 'locked').length;

  const done = person.done || 0;
  const open = person.open || 0;
  const draft = person.draft || 0;
  const miss = person.miss || 0;
  const total = done + open + draft + miss;
  const unsigned = open + draft + miss;

  // Never round up to 100 while anything is outstanding — that single pixel of rounding would
  // print "fully compliant" over an open finding.
  let pct = total ? Math.round((done / total) * 100) : 0;
  if (done < total && pct >= 100) pct = 99;

  const band = total === 0 ? 'none' : miss > 0 ? 'bad' : unsigned > 0 ? 'warn' : 'ok';
  const verdict = total === 0
    ? 'No controlled document in scope names this role.'
    : band === 'ok'
      ? 'Compliant — every required training is signed.'
      : band === 'bad'
        ? `Action required — ${unsigned} of ${total} required trainings are not signed.`
        : `In progress — ${unsigned} of ${total} required trainings are not yet signed.`;

  const sdoc = (single && single.doc) || null;
  const stask = (single && single.task) || null;

  const stamp = stampOf(builtAt);
  const uidPart = String(person.uid || '').toUpperCase().replace(/[^A-Z0-9]+/g, '') || 'USER';
  // The one-training record is identified by its EVIDENCE (the task), so two certificates for
  // the same person on different documents can never collide.
  const evidence = single
    ? String((stask && stask.tk) || (sdoc && sdoc.key) || '').toUpperCase().replace(/[^A-Z0-9]+/g, '')
    : '';
  const certId = `TRN-CERT-${uidPart}${evidence ? `-${evidence}` : ''}-${stamp || 'NOSNAPSHOT'}`;

  const tenant = profile?.tenant || '';
  // Only an explicit Approved fills the line, and only with a name to put on it.
  const qaApproved = !!(approval && approval.approved && approval.name);
  const pctOf = (n) => (total ? (n / total) * 100 : 0);

  return (
    <div className="trc-overlay" role="dialog" aria-modal="true" aria-label="Training certificate">
      <div className="trc-bar">
        <div className="trc-bar-title">
          {single ? 'Training Completion Certificate' : 'Training Certificate'}{' '}
          <span>
            · {person.name || person.uid}
            {single && sdoc ? ` · ${sdoc.key}` : ''}
          </span>
        </div>
        <div className="trc-bar-actions">
          <button type="button" className="trc-btn trc-btn-primary" onClick={print}>
            ⎙ Print / Save as PDF
          </button>
          <button type="button" className="trc-btn" onClick={onClose} aria-label="Close">Close</button>
        </div>
      </div>

      <article className="trc-sheet">

        <header className="trc-head">
          <div>
            <div>Orcanos QMS{tenant ? <b> · {tenant}</b> : null}</div>
            <div>Panel: <b>{panelName || 'Training Traceability'}</b></div>
          </div>
          {/* No "Page 1 of 1": a record listing 40-odd documents runs to several sheets, and a
              page count we cannot compute in CSS would be a false statement on a compliance
              document. The browser's own print footer numbers the pages truthfully. */}
          <div className="trc-right">
            <div>Certificate <b>{certId}</b></div>
          </div>
        </header>

        <div className="trc-title-block">
          <p className="trc-kicker">Quality Management System · Training Record</p>
          <h1 className="trc-h1">
            {single ? 'Training Completion Certificate' : 'Training Compliance Certificate'}
          </h1>
          {single ? (
            <p className="trc-attest">
              This certifies that <em>{person.name || person.uid}</em> completed the required
              read-and-understand training for <em>{(sdoc && sdoc.name) || (sdoc && sdoc.key)}</em>
              {sdoc && sdoc.key ? <> ({sdoc.key})</> : null}
              {stask && stask.rev ? <>, revision <em>{stask.rev}</em></> : null}
              {stask && stask.sd ? <>, and signed it in Orcanos on <em>{stask.sd}</em></> : <> and signed it in Orcanos</>}
              . The signature is recorded on training task{' '}
              <em>{(stask && stask.tk) || '—'}</em>.
            </p>
          ) : (
            <p className="trc-attest">
              This is a system-generated record of the controlled documents that name a role held by{' '}
              <em>{person.name || person.uid}</em>, and the state of that person&rsquo;s training task
              for each one, as held in Orcanos
              {builtAt ? <> at <em>{formatStamp(builtAt)}</em></> : null}. Every entry below is
              traceable to the training task identified beside it.
            </p>
          )}
        </div>

        <section className="trc-block">
          <h2 className="trc-h2">Trainee</h2>
          <div className="trc-ledger">
            <Field label="Full name" value={person.name || person.uid} />
            <Field label="User ID" value={person.uid} mono />
            <Field label="User profile" value={profile?.key || person.pkey} mono />
            <Field label="Role / job title" value={person.role} />
            {(profile?.fields || []).map((f) => (
              <Field key={f.label} label={f.label} value={f.value} />
            ))}
            {loadingProfile && <div className="trc-fld trc-loading">Reading profile…</div>}
          </div>
          {!loadingProfile && profile?.reason && (
            <p className="trc-note">{profile.reason}</p>
          )}
        </section>

        {single && (
          <section className="trc-block">
            <h2 className="trc-h2">The training</h2>
            <div className="trc-ledger">
              <Field label="Document" value={(sdoc && sdoc.key) || '—'} mono />
              <Field label="Title" value={(sdoc && sdoc.name) || '—'} />
              <Field label="Revision signed" value={(stask && stask.rev) || (sdoc && sdoc.rev)} mono />
              <Field label="Training task" value={(stask && stask.tk) || '—'} mono />
              <Field label="Assigned" value={(stask && stask.ad) || '—'} mono />
              {/* No due date: this training IS signed, so when it was due says nothing about the
                  record. The full compliance certificate keeps its Due column, where rows that
                  are still outstanding make it meaningful. */}
              <Field label="Signed" value={(stask && stask.sd) || '—'} mono />
              <Field label="Routing state" value={(stask && stask.ts) || 'Approved'} />
            </div>
            <p className="trc-scope trc-single-basis">
              <b>Signed</b> means this training task&rsquo;s routing state is <code>Approved</code>{' '}
              in Orcanos. This certificate records <b>one training on one revision</b> — it is not
              a statement about this person&rsquo;s other training obligations. For their full
              standing, use the Training Compliance Certificate on their training file.
            </p>
          </section>
        )}

        {!single && (
        <>
        <section className="trc-block">
          <h2 className="trc-h2">Compliance score</h2>
          <div className={`trc-score trc-b-${band}`}>
            <div>
              <div className="trc-score-num">{pct}<small>%</small></div>
              <div className="trc-score-den">{done} signed ÷ {total} required</div>
            </div>
            <div>
              <div className="trc-verdict">{verdict}</div>
              <div
                className="trc-bar-graph"
                role="img"
                aria-label={`${done} signed, ${open + draft} open, ${miss} with no task, of ${total}`}
              >
                <i className="trc-s-ok" style={{ width: `${pctOf(done)}%` }} />
                <i className="trc-s-open" style={{ width: `${pctOf(open + draft)}%` }} />
                <i className="trc-s-miss" style={{ width: `${pctOf(miss)}%` }} />
              </div>
              <div className="trc-tally">
                <span><i className="trc-dot trc-d-ok" />{done} signed</span>
                <span><i className="trc-dot trc-d-open" />{open + draft} task open</span>
                <span><i className="trc-dot trc-d-miss" />{miss} no task assigned</span>
              </div>
              {/* Stated separately, and never inside the percentage above. The score is the
                  signature rate; a quiz is a second question with a smaller denominator (only
                  the trainings that have one, and only once the task is signed). Merging them
                  would silently change what every existing certificate claims. */}
              {anyQuiz && (
                <div className="trc-quizline">
                  <b>Quiz:</b> {quizPassed} of {quizOwed} passed
                  {quizOwed > quizPassed && ' — the outstanding ones are named in the record below'}.
                  {' '}A quiz checks what was understood; it is not a signature, and the score
                  above counts signatures only.
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="trc-block">
          <h2 className="trc-h2">Scope &amp; basis</h2>
          <p className="trc-scope">
            <b>Population:</b> every controlled document with an approved revision whose Roles field
            names <b>{person.role || 'this person’s role'}</b>. <b>Revision:</b> the current
            approved revision only; superseded revisions are out of scope.{' '}
            <b>Signed</b> means the training task&rsquo;s routing state is <code>Approved</code>;{' '}
            <b>In work</b> means a task exists and is not yet approved; <b>No task</b> means the
            document names this role and no training task was ever created — the finding this
            report exists to surface. Read-and-understand training only; formal training recorded
            outside the document-control workflow is not covered.
          </p>
        </section>

        <section className="trc-block">
          <h2 className="trc-h2">
            Training record — {total} {total === 1 ? 'document' : 'documents'}
          </h2>
          <div className="trc-scroll">
            <table className="trc-rec">
              <thead>
                <tr>
                  <th>Document</th><th>Rev</th><th>Training task</th>
                  <th>Assigned</th><th>Due</th><th>Signed</th><th>Status</th>
                  {anyQuiz && <th>Quiz</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((d, i) => {
                  const pill = STATE_PILL[d.st] || STATE_PILL.miss;
                  return (
                    <tr key={`${d.k}-${i}`}>
                      <td>
                        <div className="trc-doc-t">{d.n || '—'}</div>
                        <div className="trc-k">
                          {d.url
                            ? <a href={d.url} target="_blank" rel="noopener noreferrer">{d.k}</a>
                            : d.k}
                        </div>
                      </td>
                      <td className="trc-k">{dash(d.rev)}</td>
                      <td className="trc-k">
                        {d.tk
                          ? (d.turl
                            ? <a href={d.turl} target="_blank" rel="noopener noreferrer">{d.tk}</a>
                            : d.tk)
                          : <span className="trc-muted">— none —</span>}
                      </td>
                      <td className="trc-k trc-muted">{dash(d.ad)}</td>
                      <td className="trc-k">{dash(d.due)}</td>
                      <td className="trc-k">{dash(d.sd)}</td>
                      <td><span className={`trc-pill trc-${pill.cls}`}>{pill.label}</span></td>
                      {/* Printed verbatim like every other fact here — a pass with its mark, a
                          failure named as one, and a quiz that has not opened said plainly
                          rather than left blank beside a signature. */}
                      {anyQuiz && (
                        <td className="trc-k">
                          {!d.q ? <span className="trc-muted">— none —</span>
                            : d.q.state === 'passed'
                              ? <span className="trc-pill trc-p-ok">
                                  Passed{d.q.score != null ? ` ${d.q.score}%` : ''}
                                </span>
                              : d.q.state === 'locked'
                                ? <span className="trc-muted">not open yet</span>
                                : (
                                  <span className={`trc-pill trc-${d.q.state === 'exhausted' ? 'p-miss' : 'p-open'}`}>
                                    {{ failed: 'Not passed', exhausted: 'No attempts left',
                                       in_progress: 'In progress',
                                       not_started: 'Not started' }[d.q.state] || 'Not passed'}
                                  </span>
                                )}
                        </td>
                      )}
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={anyQuiz ? 8 : 7} className="trc-muted">No documents in scope.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        </>
        )}

        {/* There is deliberately no separate "Open items" block. The record table above already
            prints EVERY document with its own status pill, and the score band prints the counts,
            so a second list of the unsigned ones repeated the same rows in a different order —
            on a real record that was 33 of 44 entries said twice. The rule this feature is built
            on is that gaps are never summarised away; that rule is satisfied by the table, which
            names every gap in place, not by restating them. */}

        <section className="trc-block">
          <h2 className="trc-h2">Review &amp; approval</h2>
          <div className={`trc-sigs${single ? ' trc-sigs-2' : ''}`}>
            {/* The trainee's signature is REPRODUCED, not collected, on the one-training record:
                Orcanos already holds their electronic signature and this sheet transcribes it,
                which is why it is captioned as an electronic signature and dated from the task.
                The full compliance certificate has no single signing event to transcribe, so it
                keeps an empty rule — rendering a name there would assert a signature that was
                never given. */}
            <Signature
              role="Trainee"
              name={person.name || person.uid}
              sub={person.role}
              signedName={single ? (person.name || person.uid) : null}
              signedAt={single ? (stask && stask.sd) : null}
            />
            {!single && <Signature role="Training coordinator" />}
            <Signature
              role="Quality assurance"
              name={qaApproved ? approval.name : null}
              signedName={qaApproved ? approval.name : null}
              signedAt={qaApproved ? approval.date : null}
              capLabel={qaApproved
                ? `Approved on ${approval.trn || 'the training record'}`
                : null}
            />
          </div>
        </section>

        <footer className="trc-foot">
          {/* The snapshot's build time is not lost by dropping its line here — it is encoded in
              the certificate id on the right (…-20260826-1839), which is what makes reprints of
              one snapshot identical and citable. */}
          <div>
            <div>Generated {formatStamp(new Date().toISOString())}</div>
          </div>
          <div className="trc-right">
            <div className="trc-warnmark">Uncontrolled when printed</div>
            <div>{certId}</div>
          </div>
        </footer>

      </article>
    </div>
  );
}

function Field({ label, value, mono }) {
  const text = String(value == null ? '' : value).trim();
  if (!text) return null;                 // a tenant that doesn't carry the field shows nothing
  return (
    <div className="trc-fld">
      <div className="trc-l">{label}</div>
      <div className={`trc-v${mono ? ' trc-mono' : ''}`}>{text}</div>
    </div>
  );
}

function Signature({ role, name, sub, signedName, signedAt, capLabel }) {
  const esign = !!signedName;
  return (
    <div className={`trc-sig${esign ? ' trc-sig-esign' : ''}`}>
      <div className="trc-role">{role}</div>
      {esign && <div className="trc-esign-name">{signedName}</div>}
      <div className="trc-line" />
      <div className="trc-cap">
        <span>{capLabel || (esign ? 'Signed electronically in Orcanos' : 'Signature')}</span>
        <span>{esign ? (signedAt || '—') : 'Date'}</span>
      </div>
      <div className="trc-name">
        {name || ' '}{sub ? <span> · {sub}</span> : (!name ? <span>Print name</span> : null)}
      </div>
    </div>
  );
}

export default TrainingCertificate;
